import { drawClipperSprite } from './clipperSprites';
import {
  CLIPPER_DUCK_HEIGHT,
  CLIPPER_HEIGHT,
  CLIPPER_WIDTH,
  CLIPPER_X,
  GROUND_Y,
  RUN_HEIGHT,
  RUN_WIDTH,
  clipperRunScore,
  type ClipperRunState,
  type Obstacle,
} from '@/lib/loading-game/clipperRun';

export const CLIPPER_GREEN = '#087030';
const CLIPPER_GREEN_DARK = '#054422';
const WHITE = '#ffffff';

export interface ClipperRunPalette {
  background: string;
  foreground: string;
  mutedForeground: string;
  primary: string;
}

export interface ClipperRunLabels {
  best: string;
  gameOver: string;
}

export function drawClipperRun(
  ctx: CanvasRenderingContext2D,
  state: ClipperRunState,
  frame: {
    now: number;
    sprites?: CanvasImageSource | null;
    best: number;
    reducedMotion: boolean;
    palette: ClipperRunPalette;
    labels: ClipperRunLabels;
  },
): void {
  const { palette } = frame;
  ctx.clearRect(0, 0, RUN_WIDTH, RUN_HEIGHT);
  ctx.fillStyle = palette.background;
  ctx.fillRect(0, 0, RUN_WIDTH, RUN_HEIGHT);

  drawBackdrop(ctx, state.distance);
  drawGround(ctx, state.distance);
  for (const obstacle of state.obstacles) drawObstacle(ctx, obstacle, state.distance);
  if (frame.sprites) {
    drawClipperSprite(ctx, frame.sprites, state);
  } else {
    drawClipper(ctx, state, frame.now, frame.reducedMotion);
  }
  drawHud(ctx, state, frame);
}

function drawBackdrop(ctx: CanvasRenderingContext2D, distance: number): void {
  ctx.save();
  ctx.strokeStyle = 'rgba(8, 112, 48, 0.16)';
  ctx.lineWidth = 1;
  const grid = 32;
  const shift = distance * 0.2 % grid;
  for (let x = -shift; x <= RUN_WIDTH; x += grid) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, GROUND_Y);
    ctx.stroke();
  }
  for (let y = 16; y < GROUND_Y; y += grid) {
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(RUN_WIDTH, y);
    ctx.stroke();
  }
  ctx.restore();

  ctx.fillStyle = 'rgba(255, 255, 255, 0.05)';
  const clouds = [
    { x: 90, y: 32, scale: 1 },
    { x: 310, y: 22, scale: 0.72 },
    { x: 520, y: 40, scale: 0.88 },
  ];
  for (const cloud of clouds) {
    const span = RUN_WIDTH + 120;
    const x = ((cloud.x - distance * 0.12) % span + span) % span - 60;
    drawCloud(ctx, x, cloud.y, cloud.scale);
  }
}

function drawCloud(ctx: CanvasRenderingContext2D, x: number, y: number, scale: number): void {
  ctx.beginPath();
  ctx.ellipse(x, y, 18 * scale, 8 * scale, 0, 0, Math.PI * 2);
  ctx.ellipse(x + 14 * scale, y + 2 * scale, 12 * scale, 7 * scale, 0, 0, Math.PI * 2);
  ctx.ellipse(x - 14 * scale, y + 3 * scale, 10 * scale, 6 * scale, 0, 0, Math.PI * 2);
  ctx.fill();
}

function drawGround(ctx: CanvasRenderingContext2D, distance: number): void {
  ctx.save();
  ctx.strokeStyle = CLIPPER_GREEN;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(0, GROUND_Y + 0.5);
  ctx.lineTo(RUN_WIDTH, GROUND_Y + 0.5);
  ctx.stroke();

  ctx.fillStyle = CLIPPER_GREEN;
  const period = 46;
  const scroll = distance % period;
  for (let x = -scroll; x < RUN_WIDTH; x += period) {
    ctx.fillRect(x + 8, GROUND_Y + 5, 14, 2);
    ctx.fillRect(x + 28, GROUND_Y + 5, 5, 2);
  }
  ctx.restore();
}

function drawObstacle(ctx: CanvasRenderingContext2D, obstacle: Obstacle, distance: number): void {
  if (obstacle.kind === 'bird-low' || obstacle.kind === 'bird-high') {
    drawBird(ctx, obstacle, distance);
    return;
  }
  drawCactus(ctx, obstacle);
}

function drawCactus(ctx: CanvasRenderingContext2D, obstacle: Obstacle): void {
  const { x, width, height } = obstacle;
  const top = GROUND_Y - height;
  ctx.save();
  ctx.fillStyle = CLIPPER_GREEN;
  ctx.strokeStyle = CLIPPER_GREEN_DARK;
  ctx.lineWidth = 1.5;
  const trunkW = Math.max(6, width * 0.34);
  const trunkX = x + (width - trunkW) / 2;
  fillRound(ctx, trunkX, top + 2, trunkW, height - 2, 3);

  const armW = Math.max(5, width * 0.28);
  const armY = top + height * 0.42;
  fillRound(ctx, x, armY, width * 0.42, 5, 2);
  fillRound(ctx, x, top + height * 0.18, armW, height * 0.28, 2);
  fillRound(ctx, x + width * 0.58, armY + 6, width * 0.42, 5, 2);
  fillRound(ctx, x + width - armW, top + height * 0.28, armW, height * 0.24, 2);
  ctx.restore();
}

function drawBird(ctx: CanvasRenderingContext2D, obstacle: Obstacle, distance: number): void {
  const bottom = GROUND_Y - obstacle.lift;
  const midY = bottom - obstacle.height * 0.45;
  const flap = Math.sin(distance / 7) * (obstacle.height * 0.45);
  ctx.save();
  ctx.fillStyle = CLIPPER_GREEN;
  ctx.strokeStyle = CLIPPER_GREEN_DARK;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.ellipse(
    obstacle.x + obstacle.width * 0.58,
    midY,
    obstacle.width * 0.28,
    obstacle.height * 0.38,
    0,
    0,
    Math.PI * 2,
  );
  ctx.fill();
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(obstacle.x + obstacle.width * 0.45, midY);
  ctx.quadraticCurveTo(
    obstacle.x + obstacle.width * 0.2,
    midY - obstacle.height * 0.2 - flap,
    obstacle.x + 2,
    midY - flap * 0.3,
  );
  ctx.quadraticCurveTo(
    obstacle.x + obstacle.width * 0.22,
    midY + obstacle.height * 0.35,
    obstacle.x + obstacle.width * 0.48,
    midY + 1,
  );
  ctx.fill();

  ctx.fillStyle = '#d6ff4a';
  ctx.beginPath();
  ctx.moveTo(obstacle.x + obstacle.width - 2, midY - 1);
  ctx.lineTo(obstacle.x + obstacle.width + 6, midY + 1);
  ctx.lineTo(obstacle.x + obstacle.width - 2, midY + 3);
  ctx.fill();
  ctx.restore();
}

function drawClipper(
  ctx: CanvasRenderingContext2D,
  state: ClipperRunState,
  now: number,
  reducedMotion: boolean,
): void {
  const duck = state.ducking;
  const jumping = state.feetY < GROUND_Y - 0.5;
  const dead = state.phase === 'dead';
  const running = state.phase === 'playing' && !jumping && !duck;
  const idle = state.phase === 'ready';
  const stride = running
    ? Math.sin(state.distance / 13)
    : jumping
      ? 0.85
      : duck
        ? 0.15
        : 0.45;
  const bob = idle && !reducedMotion ? Math.sin(now / 280) * 2.4 : 0;

  ctx.save();
  ctx.translate(CLIPPER_X + CLIPPER_WIDTH / 2, state.feetY + bob);
  if (dead) ctx.rotate(-0.18);

  ctx.fillStyle = 'rgba(8, 112, 48, 0.18)';
  const lift = Math.max(0, GROUND_Y - state.feetY);
  ctx.beginPath();
  ctx.ellipse(0, -bob + 2, Math.max(8, 15 - lift * 0.06), 3.2, 0, 0, Math.PI * 2);
  ctx.fill();

  if (duck) {
    ctx.translate(0, -4);
    ctx.scale(1.08, CLIPPER_DUCK_HEIGHT / CLIPPER_HEIGHT);
  }

  const lead = running ? (stride > 0 ? 1 : -1) : 1;
  drawShoe(ctx, -7 - stride * 6, jumping ? -6 : -5, 0.72, lead < 0);
  drawShoe(ctx, 8 + stride * 6, jumping ? -11 : -6, 1, lead > 0);

  const bodyW = 32;
  const bodyH = 30;
  const bodyX = -bodyW / 2;
  const bodyY = -CLIPPER_HEIGHT + 8;
  ctx.fillStyle = WHITE;
  ctx.strokeStyle = CLIPPER_GREEN;
  ctx.lineWidth = 2.6;
  fillStrokeRound(ctx, bodyX, bodyY, bodyW, bodyH, 8);

  ctx.fillStyle = CLIPPER_GREEN;
  fillRound(ctx, bodyX + 3.2, bodyY + 5, 2.4, bodyH - 12, 1.2);
  fillRound(ctx, bodyX + bodyW - 5.6, bodyY + 5, 2.4, bodyH - 12, 1.2);

  drawGlove(ctx, bodyX + 2, bodyY + 11, -1, dead ? 1 : 15 + stride * 2);
  drawGlove(ctx, bodyX + bodyW - 2, bodyY + 18, 1, dead ? -2 : -11);
  drawFace(ctx, bodyY, dead);

  ctx.restore();
}

function drawShoe(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  scale: number,
  striped: boolean,
): void {
  ctx.save();
  ctx.strokeStyle = CLIPPER_GREEN;
  ctx.fillStyle = WHITE;
  ctx.lineWidth = 1.7;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(x * 0.25, -16);
  ctx.quadraticCurveTo(x * 0.55, -9, x, y - 3);
  ctx.stroke();

  const rx = 8.2 * scale;
  const ry = 3.8 * scale;
  ctx.beginPath();
  ctx.ellipse(x + 2 * scale, y, rx, ry, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  if (striped) {
    ctx.lineWidth = 1.15;
    for (let row = 0; row < 3; row += 1) {
      const lineY = y - ry * 0.42 + row * ry * 0.38;
      ctx.beginPath();
      ctx.moveTo(x + 2 * scale - rx * 0.7, lineY);
      ctx.lineTo(x + 2 * scale + rx * 0.48, lineY);
      ctx.stroke();
    }
  }
  ctx.restore();
}

function drawGlove(
  ctx: CanvasRenderingContext2D,
  originX: number,
  originY: number,
  direction: number,
  lift: number,
): void {
  const gloveX = originX + direction * 8;
  const gloveY = originY - lift;
  ctx.save();
  ctx.strokeStyle = CLIPPER_GREEN;
  ctx.fillStyle = WHITE;
  ctx.lineWidth = 2.1;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(originX, originY);
  ctx.quadraticCurveTo(originX + direction * 7, originY - lift * 0.35, gloveX, gloveY);
  ctx.stroke();
  ctx.lineWidth = 1.7;
  ctx.beginPath();
  ctx.arc(gloveX, gloveY, 4.6, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  ctx.restore();
}

function drawFace(ctx: CanvasRenderingContext2D, bodyY: number, dead: boolean): void {
  const eyeY = bodyY + 11;
  ctx.save();
  ctx.strokeStyle = CLIPPER_GREEN;
  ctx.fillStyle = CLIPPER_GREEN;
  ctx.lineWidth = 1.7;
  ctx.lineCap = 'round';
  if (dead) {
    for (const dx of [-4.4, 4.6]) {
      ctx.beginPath();
      ctx.arc(dx, eyeY + 1, 3.2, 0.12 * Math.PI, 0.88 * Math.PI);
      ctx.stroke();
    }
  } else {
    ctx.beginPath();
    ctx.ellipse(-4.4, eyeY, 2.15, 4.3, 0, 0, Math.PI * 2);
    ctx.ellipse(4.6, eyeY, 2.15, 4.3, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  const noseY = eyeY + 6.5;
  ctx.beginPath();
  ctx.moveTo(-0.4, noseY);
  ctx.quadraticCurveTo(4.6, noseY + 1.2, 0.8, noseY + 4.4);
  ctx.stroke();

  ctx.beginPath();
  if (dead) {
    ctx.arc(0, noseY + 12, 5.5, 1.15 * Math.PI, 1.85 * Math.PI);
  } else {
    ctx.arc(0, noseY + 8, 5.2, 0.2 * Math.PI, 0.8 * Math.PI);
  }
  ctx.stroke();
  ctx.restore();
}

function drawHud(
  ctx: CanvasRenderingContext2D,
  state: ClipperRunState,
  frame: { best: number; palette: ClipperRunPalette; labels: ClipperRunLabels },
): void {
  const score = String(clipperRunScore(state)).padStart(5, '0');
  ctx.font = '600 13px ui-monospace, SFMono-Regular, Menlo, monospace';
  ctx.textBaseline = 'top';
  ctx.textAlign = 'right';
  const right = RUN_WIDTH - 12;
  ctx.fillStyle = frame.palette.primary;
  ctx.fillText(score, right, 10);
  if (frame.best > 0) {
    const bestText = `${frame.labels.best} ${String(frame.best).padStart(5, '0')}`;
    ctx.fillStyle = frame.palette.mutedForeground;
    ctx.fillText(bestText, right - ctx.measureText(score).width - 16, 10);
  }

  if (state.phase === 'dead') {
    ctx.fillStyle = frame.palette.foreground;
    ctx.font = '700 16px ui-sans-serif, system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(frame.labels.gameOver, RUN_WIDTH / 2, 36);
  }
}

function fillRound(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): void {
  traceRound(ctx, x, y, width, height, radius);
  ctx.fill();
}

function fillStrokeRound(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): void {
  traceRound(ctx, x, y, width, height, radius);
  ctx.fill();
  ctx.stroke();
}

function traceRound(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): void {
  const r = Math.max(0, Math.min(radius, width / 2, height / 2));
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + width, y, x + width, y + height, r);
  ctx.arcTo(x + width, y + height, x, y + height, r);
  ctx.arcTo(x, y + height, x, y, r);
  ctx.arcTo(x, y, x + width, y, r);
  ctx.closePath();
}
